import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AuditTypeSummary } from "./audit-types";

export interface AuditFiltersState {
  filters: Record<string, string>;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  includeSensitive: boolean;
}

interface AuditFiltersPanelProps {
  type: AuditTypeSummary;
  values: AuditFiltersState;
  onApply: (values: AuditFiltersState) => void;
  onClear: () => void;
}

const AuditFiltersPanel = ({
  type,
  values,
  onApply,
  onClear,
}: AuditFiltersPanelProps) => {
  const [draft, setDraft] = useState<AuditFiltersState>(values);

  useEffect(() => {
    setDraft(values);
  }, [values]);

  const filterableColumns = type.columns.filter(
    (column) => column.isFilterable,
  );
  const hasSensitiveColumns = type.columns.some((column) => column.isSensitive);

  const handleFilterChange = (columnName: string, value: string) => {
    setDraft((current) => ({
      ...current,
      filters: { ...current.filters, [columnName]: value },
    }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #dfe6f0",
        borderRadius: 1.5,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 2, sm: 2.5 }, py: 1.75 }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <FilterAltOutlinedIcon sx={{ color: "#1c2d49", fontSize: 21 }} />
          <Typography sx={{ color: "#1c2d49", fontWeight: 700 }}>
            Filters
          </Typography>
        </Stack>
        <Button
          onClick={onClear}
          startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
          sx={{ color: "#1769ff", fontWeight: 600, textTransform: "none" }}
        >
          Clear all
        </Button>
      </Stack>
      <Divider />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(200px, 1fr))",
          },
          gap: { xs: 2, sm: 2.5 },
          alignItems: "start",
          px: { xs: 2, sm: 2.5 },
          py: 2.5,
        }}
      >
        {filterableColumns.map((column) => (
          <TextField
            key={column.columnName}
            label={column.displayName}
            size="small"
            value={draft.filters[column.columnName] ?? ""}
            onChange={(event) =>
              handleFilterChange(column.columnName, event.target.value)
            }
          />
        ))}

        {type.dateRangeColumn && (
          <Stack spacing={0.8}>
            <Typography
              variant="caption"
              sx={{ color: "#24344d", fontWeight: 700 }}
            >
              Date range
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <DatePicker
                label="From"
                value={draft.dateFrom}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, dateFrom: value }))
                }
                maxDate={draft.dateTo ?? undefined}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
              <DatePicker
                label="To"
                value={draft.dateTo}
                onChange={(value) =>
                  setDraft((current) => ({ ...current, dateTo: value }))
                }
                minDate={draft.dateFrom ?? undefined}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Box>
          </Stack>
        )}

        {hasSensitiveColumns && (
          <FormControlLabel
            control={
              <Switch
                checked={draft.includeSensitive}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    includeSensitive: event.target.checked,
                  }))
                }
              />
            }
            label="Show sensitive columns"
          />
        )}
      </Box>

      <Divider />
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}
      >
        <Button variant="contained" onClick={() => onApply(draft)}>
          Apply
        </Button>
      </Stack>
    </Paper>
  );
};

export default AuditFiltersPanel;
